import { DatePicker, Input, Select } from 'antd';
import { Dayjs } from 'dayjs';
import FilterIcon from 'icons/search-filter.svg';
import SearchIcon from 'icons/search-icon.svg';
import debounce from 'lodash/debounce';
import dayjs from 'mixins/dayjsConfig';
import { localize } from 'mixins/helpers';
import Button from 'mixins/ui/button/button';
import PartWrapper from 'mixins/ui/part-wrapper';
import React, { useEffect, useMemo, useState } from 'react';
import { checkAuthorization } from '/react4xp/mixins/check-authorization/check-authorization';
import { getQuestionnaireList } from '/react4xp/parts/questionnaire-ogd/actions';
import {
    Props,
    Survey,
    TQuestions,
} from '/react4xp/parts/questionnaire-ogd/questionnaire-ogd.type';
import Questions from '/react4xp/parts/questionnaire-ogd/Questions';
import Results from '/react4xp/parts/questionnaire-ogd/Results';
import Stats from '/react4xp/parts/questionnaire-ogd/Stats';
import Theme from '/react4xp/parts/questionnaire-ogd/Theme';

import './questionnaire-ogd.scss';

export const dateFormat = 'DD.MM.YYYY';
export const searchDateFormat = 'YYYY-MM-DD';
export const statuses = (translations: any) => [
    { label: localize('oprosy-new', translations), value: 'ACTIVE' },
    { label: localize('oprosy-completed', translations), value: 'COMPLETED' },
    { label: localize('oprosy-passed', translations), value: 'PASSED' },
];
const QuestionnaireOgd: React.FC<Props> = (props: any) => {
    const { statusCMS, periodCMS } = props.config;

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [step, setStep] = useState<'MAIN' | 'QUESTIONS' | 'STATS'>('MAIN');
    const [survey, setSurvey] = useState<Survey>();
    const [answers, setAnswers] = useState<Array<TQuestions>>();
    const [dates, setDates] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
    const [status, setStatus] = useState<string | undefined>(undefined);
    const [searchText, setSearchText] = useState<string | undefined>(undefined);
    const [surveys, setSurveys] = useState<Array<Survey>>([]);
    const [statsData, setStatsData] = useState<TQuestions[]>(null);
    const [showExtendedSearch, setShowExtendedSearch] = useState<boolean>(false);

    useEffect(() => {
        if (step === 'MAIN') {
            fetchData(searchText, dates, status);
        }
    }, [step, searchText, dates, status]);

    const debouncedSetSearchText = useMemo(
        () =>
            debounce((value: string) => {
                setSearchText(value);
                setSearchText;
                fetchData(value, dates, status);
            }, 500),
        [dates, status]
    );

    useEffect(() => {
        return () => {
            debouncedSetSearchText.cancel();
        };
    }, [debouncedSetSearchText]);

    const handleShowExtendedSearch = () => {
        setShowExtendedSearch(!showExtendedSearch);
    };

    const fetchData = async (
        searchText?: string,
        dates?: [dayjs.Dayjs | null, dayjs.Dayjs | null],
        status?: string | undefined
    ) => {
        // const isVerified = await executeRecaptcha('questionnaire_ogd');
        // if (isVerified) {
        setIsLoading(true);
        const params = {
            ...(searchText && { searchText }),
            ...(dates && {
                beginDate: dates[0]?.format(searchDateFormat) || '',
                endDate: dates[1]?.format(searchDateFormat) || '',
            }),
            ...(status && { status }),
        };
        getQuestionnaireList(props.token, props.language.slice(0, 2).toUpperCase(), params)
            .then((res) => {
                setSurveys(res.data.content);
            })
            .catch((error) =>
                checkAuthorization(
                    error,
                    props.language,
                    'questionnaire-ogd',
                    props.commonTranslations
                )
            )
            .finally(() => {
                setIsLoading(false);
            });
        // }
    };

    const disabledDate = (current: Dayjs) => {
        return current.year() < 1900 || (current && current > dayjs().endOf('day'));
    };

    const handleReset = () => {
        setSearchText(undefined);
        setStatus(undefined);
        setDates(undefined);
    };

    return (
        <div className="service-filter-block">
            {step === 'MAIN' && (
                <>
                    <div className="search-bar">
                        <Input.Search
                            allowClear
                            placeholder={localize('oprosy-search-by-name', props.translations)}
                            enterButton
                            value={searchText}
                            onChange={(e) => debouncedSetSearchText(e.target.value)}
                            onSearch={() => fetchData(searchText)}
                            prefix={<SearchIcon />}
                            suffix={
                                <div
                                    className="search-bar__suffix"
                                    onClick={handleShowExtendedSearch}
                                >
                                    <FilterIcon style={{ width: 'auto', height: 'auto' }} />
                                    <Button color="borderless">
                                        {localize('oprosy-advanced-search', props.translations)}
                                    </Button>
                                </div>
                            }
                        />
                    </div>
                    {showExtendedSearch && (
                        <div className="extended-search">
                            <div className="extended-search__search-row">
                                <div className="extended-search__search-field">
                                    <label htmlFor="status">{statusCMS + ':'}</label>
                                    <Select
                                        placeholder={localize(
                                            'placeholder.select',
                                            props.commonTranslations
                                        )}
                                        allowClear
                                        value={status}
                                        options={statuses(props.translations).map((status, id) => ({
                                            key: id,
                                            value: status.value,
                                            label: status.label,
                                        }))}
                                        onChange={(value) => setStatus(value)}
                                    />
                                </div>
                                <div className="extended-search__search-field">
                                    <label htmlFor="date-range">{periodCMS + ':'}</label>
                                    <DatePicker.RangePicker
                                        format={dateFormat}
                                        value={dates}
                                        onChange={(dates) => setDates(dates)}
                                        disabledDate={disabledDate}
                                        placeholder={[
                                            localize(
                                                'placeholder.start-date',
                                                props.commonTranslations
                                            ),
                                            localize(
                                                'placeholder.end-date',
                                                props.commonTranslations
                                            ),
                                        ]}
                                    />
                                </div>
                            </div>
                            <div className="extended-search__button-row">
                                <Button onClick={handleReset}>
                                    {localize('button.reset', props.commonTranslations)}
                                </Button>
                                <Button
                                    color="gold"
                                    onClick={() => fetchData(searchText, dates, status)}
                                >
                                    {localize('button.find', props.commonTranslations)}
                                </Button>
                            </div>
                        </div>
                    )}

                    <Results
                        surveys={surveys}
                        translations={props.translations}
                        commonTranslations={props.commonTranslations}
                        language={props.language}
                        setSurvey={setSurvey}
                        setAnswers={setAnswers}
                        setStep={setStep}
                        setStatsData={setStatsData}
                        token={props.token}
                    />
                </>
            )}
            {step === 'QUESTIONS' && survey && (
                <>
                    <Theme
                        {...props}
                        setStep={setStep}
                        theme={survey.theme}
                        status={survey.status}
                    />
                    <Questions
                        questions={survey.questions}
                        answers={survey.status === 'PASSED' && answers}
                        {...props}
                        setStep={setStep}
                        surveyId={survey.id}
                        status={survey.status}
                    />
                </>
            )}

            {step === 'STATS' && survey && (
                <>
                    <Theme
                        {...props}
                        setStep={setStep}
                        theme={survey.theme}
                        anonymity={survey.anonymity}
                    />
                    <Stats
                        statsData={statsData}
                        translations={props.translations}
                        commonTranslations={props.commonTranslations}
                        language={props.language}
                        setStep={setStep}
                    />
                </>
            )}
        </div>
    );
};
export default (props) => (
    <PartWrapper {...props}>
        <QuestionnaireOgd {...props} />
    </PartWrapper>
);
